import { PauseIcon, PlayIcon } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import halloweenChatUrl from "@/assets/svg/halloweenChat.svg?url";
import { cn } from "@/lib/utils";

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Fetches the SVG and inserts it inline so its SMIL timeline can be paused. `?react` would put
 * the 347 KB file in the shared `local-assets` chunk, `<img>` can't be paused, and production's
 * CSP (`object-src 'none'`, `X-Frame-Options: DENY`) blocks `<object>`.
 */
const HalloweenAnimation = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [svg, setSvg] = useState<SVGSVGElement | null>(null);
  const [isPaused, setIsPaused] = useState(prefersReducedMotion);

  useEffect(() => {
    let isCancelled = false;
    fetch(halloweenChatUrl)
      .then((response) => response.text())
      .then((markup) => {
        const element = new DOMParser()
          .parseFromString(markup, "image/svg+xml")
          .querySelector("svg");
        if (isCancelled || !containerRef.current || !element) return;
        containerRef.current.replaceChildren(element);
        // Pause before the first paint; pausing before insertion is reset when the SVG is adopted.
        if (prefersReducedMotion()) {
          element.pauseAnimations();
        }
        setSvg(element);
      })
      // Decorative only: if the SVG fails to load, leave the space empty rather than report an error.
      .catch(() => {});
    return () => {
      isCancelled = true;
    };
  }, []);

  // SMIL animations ignore the CSS reduced-motion rules in index.css, so pause them here.
  useEffect(() => {
    if (isPaused) {
      svg?.pauseAnimations();
    } else {
      svg?.unpauseAnimations();
    }
  }, [svg, isPaused]);

  return (
    <div className="group relative w-full max-w-[247px] shrink-0">
      <div
        ref={containerRef}
        className="aspect-[991/678] w-full"
        role="img"
        aria-label="Haunted house with ghosts and bats"
      />
      <button
        type="button"
        onClick={() => setIsPaused(!isPaused)}
        aria-label={isPaused ? "Play animation" : "Pause animation"}
        className={cn(
          "absolute right-2 bottom-2 flex size-8 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white transition-opacity hover:bg-black/75",
          "opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-750",
          isPaused && "opacity-100",
        )}
      >
        {isPaused ? (
          <PlayIcon weight="fill" className="size-4" />
        ) : (
          <PauseIcon weight="fill" className="size-4" />
        )}
      </button>
    </div>
  );
};

export default HalloweenAnimation;
